/**
 * Client-side checks on the deliveries file before the gzip upload starts
 * (`core/apis/capacityPlanUpload`). The API enforces every limit below
 * authoritatively.
 */

/** `CapacityPlanningSettings.MaxUploadBytes`: 512 MiB of decompressed file. */
export const MAX_UPLOAD_SIZE_BYTES = 512 * 1024 * 1024;

/**
 * `CapacityPlanningSettings.MaxRequestBytes`: the API refuses a request body
 * above 64 MiB, and the body is the gzipped file, so this is checked after
 * compression rather than on the file the user picked.
 */
export const MAX_REQUEST_SIZE_BYTES = 64 * 1024 * 1024;

/**
 * XLSX is parsed whole in memory (ADR-0002), so a big file has to be CSV.
 * Matches `CapacityPlanningSettings.MaxXlsxUploadBytes` (50 MiB).
 */
export const MAX_XLSX_SIZE_BYTES = 50 * 1024 * 1024;

export const ACCEPTED_FILE_EXTENSIONS = ['.csv', '.xlsx'];

/** Value for the file input's `accept` attribute. */
export const ACCEPTED_FILE_ACCEPT = ACCEPTED_FILE_EXTENSIONS.join(',');

export const megaBytes = (bytes: number) => Math.round(bytes / (1024 * 1024));

/** The message the upload shows when the compressed file is still too big to send. */
export const compressedTooLargeMessage = () =>
  `The file compresses to more than ${megaBytes(MAX_REQUEST_SIZE_BYTES)} MB, which is more than one request may carry. Split it into smaller files.`;

/** The sentence the New run form shows under the file picker. */
export const LARGE_FILE_NOTE = `Files above ${megaBytes(MAX_XLSX_SIZE_BYTES)} MB must be CSV: XLSX is parsed whole in memory.`;

const extensionOf = (fileName: string) =>
  fileName.slice(fileName.lastIndexOf('.')).toLowerCase();

/** Returns a message to show the user, or null when the file is acceptable. */
export function validateCapacityPlanFile(file: File): string | null {
  const extension = extensionOf(file.name);

  if (!ACCEPTED_FILE_EXTENSIONS.includes(extension)) {
    return `Only ${ACCEPTED_FILE_EXTENSIONS.join(' and ')} files are accepted.`;
  }

  if (file.size === 0) {
    return 'The file is empty.';
  }

  if (extension === '.xlsx' && file.size > MAX_XLSX_SIZE_BYTES) {
    return LARGE_FILE_NOTE;
  }

  if (file.size > MAX_UPLOAD_SIZE_BYTES) {
    return `The file is larger than ${megaBytes(MAX_UPLOAD_SIZE_BYTES)} MB.`;
  }

  return null;
}
